import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { MESSAGE_STATE_ERROR, MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { Permissions } from 'constants/permissions';
import { useDocumentTitle } from 'hooks/document-title';
import AppPage from 'layouts/AppPage/AppPage';
import { useCSVReader } from 'react-papaparse';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { ChecksSelector } from 'components/ChecksSelector/ChecksSelector';
import { CsvTableDetails } from 'components/CsvTableDetails/CsvTableDetails';
import { Button } from 'components/FormComponents';

import { messagePopUp } from 'api/app-slice';
import { useBulkImportMutation } from 'api/candidates-api';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { getForms } from 'api/forms-slice';
import { selectForms } from 'api/forms-slice';
import { useGetRenewalConfigsQuery } from 'api/renewals-config-api';

import styles from './RenewalsImport.module.scss';

export const RenewalsImport = () => {
  useDocumentTitle('Bulk Import');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const forms = useSelector(selectForms);
  const company = useSelector(selectCompany);
  const companyChecks = useSelector(selectCompanyChecks);

  const { data: renewalConfigs } = useGetRenewalConfigsQuery({});
  const [bulkImport] = useBulkImportMutation();

  const selectorRef = useRef(null);

  const [loading, setLoading] = useState(true);

  const [renewalsCompanyChecks, setRenewalsCompanyChecks] = useState([]);
  const [csvData, setCsvData] = useState();
  const [zoneHover, setZoneHover] = useState(false);

  const [valid, setValid] = useState(false);

  const { CSVReader } = useCSVReader();

  // Initialization
  useEffect(() => {
    dispatch(getForms()).then(_ => setLoading(false));
  }, []);

  useEffect(() => {
    if (companyChecks?.length < 1 || renewalConfigs?.length < 1) return;

    renewalConfigs?.forEach(rc => {
      const companyCheck = companyChecks.find(cc => cc.type === rc.type);
      if (companyCheck) {
        if (renewalsCompanyChecks?.find(rc => rc.type === companyCheck.type)) return;
        setRenewalsCompanyChecks(prev => [...prev, companyCheck]);
      }
    });
  }, [renewalConfigs, companyChecks]);

  const handleSubmit = async checksData => {
    setLoading(true);
    let data = {
      csv_data: csvData,
      checks: checksData.checks,
      candidate_data: checksData.candidate,
      renewal: true,
    };

    const { data: response, error } = await bulkImport(data);

    setLoading(false);
    if (error) {
      dispatch(
        messagePopUp({ text: error.data.error.log, state: MESSAGE_STATE_ERROR, hide: true }),
      );
    } else {
      dispatch(
        messagePopUp({
          text: `${response.result} candidates processed`,
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
      navigate('/renewals/upcoming');
    }
  };

  const onUploadAccepted = results => {
    setCsvData(results.data);
    setZoneHover(false);
  };

  const onDragOver = event => {
    event.preventDefault();
    setZoneHover(true);
  };

  const onDragLeave = event => {
    event.preventDefault();
    setZoneHover(false);
  };

  return (
    <AppPage loading={loading} requiredPermissions={[Permissions.CandidateWrite]}>
      <div className={styles.root}>
        <AppPage.Header
          title="Renewals Import"
          subtitle="Use a spreadsheet to upload multiple candidates at once. Get started by downloading the
                    template spreadsheet below. Once the candidate details are 
                    imported you can choose the check types you need"
          className="u-divider-bottom-border"
        />
        <div className={styles.body}>
          <div className={cn(styles.content, 'card', 'card-with-border', 'u-margin-y--large')}>
            <div className={cn('card_content')}>
              <div className="u-width-100 u-flex-box u-flex-align-center u-flex-justify-end">
                <Button
                  small
                  type="outline"
                  icon="file-text"
                  onClick={() =>
                    window.open(
                      'https://cdn.checkmate.tech/checkmate-files/assets/renewals_import.csv',
                      '_blank',
                    )
                  }>
                  Download template
                </Button>
              </div>
              <div className={cn(styles.dataPicker, 'u-width-100')}>
                {csvData ? (
                  <CsvTableDetails
                    csvData={csvData}
                    onDrop={() => setCsvData(null)}
                    className="u-margin-top"
                  />
                ) : (
                  <div className={cn(styles.container, 'u-width-100', 'u-margin-top')}>
                    <CSVReader
                      onUploadAccepted={onUploadAccepted}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}>
                      {({
                        getRootProps,
                        acceptedFile,
                        ProgressBar,
                        getRemoveFileProps,
                        Remove,
                      }) => (
                        <>
                          <div
                            {...getRootProps()}
                            style={Object.assign({}, styles.zone, zoneHover && styles.zoneHover)}
                            className="u-width-100">
                            {acceptedFile ? (
                              <div style={styles.file}>
                                <div style={styles.info}>
                                  <span style={styles.size}>{acceptedFile.size}</span>
                                  <span style={styles.name}>{acceptedFile.name}</span>
                                </div>
                                <div style={styles.progressBar}>
                                  <ProgressBar />
                                </div>
                                <div {...getRemoveFileProps()} style={styles.remove}>
                                  <Remove color="red" />
                                </div>
                              </div>
                            ) : (
                              <div className={styles.dropzone}>
                                Drop CSV file here or click to upload.
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </CSVReader>
                  </div>
                )}
              </div>
              <span className="u-margin-top--small t-small">
                Below are the renewals that have already been configured, if you do not see your
                renewal, you will need to{' '}
                <Link className="u-link dark" to="/renewals/configuration">
                  configure it first
                </Link>
              </span>
              <ChecksSelector
                ref={selectorRef}
                forms={forms}
                company={company}
                companyChecks={renewalsCompanyChecks}
                setValidity={setValid}
                onSubmit={handleSubmit}
                bulk
                singleType
              />
              <Button
                className="u-width-100"
                onClick={() => selectorRef.current.submit()}
                disabled={!valid || !csvData}>
                Import
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppPage>
  );
};

export default RenewalsImport;
